// Derives the member's dispute action plan from existing records only (no new backend fields).
export const ROUND_TYPES = {
  1: ["equifax", "experian", "transunion", "furnisher", "debt_validation"],
  2: ["mov"],
  3: ["follow_up", "arbitration", "cfpb"],
};
export const ROUND_PRESETS = { 1: ["equifax", "experian", "transunion"], 2: ["mov"], 3: ["follow_up"] };

export const LS = { READY: "ready", MAILED: "mailed", WAITING: "waiting", RESPONSE: "response", NEXT: "next_round", DONE: "completed" };
export const LETTER_STATUS_ORDER = [LS.READY, LS.MAILED, LS.WAITING, LS.RESPONSE, LS.NEXT, LS.DONE];
export const LETTER_STATUS_LABEL = {
  ready: "Ready to Review", mailed: "Mailed", waiting: "Waiting for Response",
  response: "Response Received", next_round: "Next Round Available", completed: "Completed",
};

export const ROUND_STATE_LABEL = {
  not_started: "Not Started", current: "Current Step", ready: "Letters Ready", mailed: "Mailed",
  waiting: "Waiting for Response", response: "Response Received", available: "Available", completed: "Completed",
};

export function roundOf(doc) {
  if (doc?.letter_type === "library") return doc.library_round || null;
  for (const r of [1, 2, 3]) if (ROUND_TYPES[r].includes(doc?.letter_type)) return r;
  return null;
}

const SUGGESTED = { 1: "Credit Bureau Accuracy and Completeness Dispute", 2: "Method/Procedure of Verification Request", 3: "Evidence-Based Credit Bureau Dispute" };
export function suggestedLetter(rounds) {
  const open = rounds.filter((r) => !r.locked).map((r) => r.round);
  const r = Math.max(1, ...open);
  return { round: r, name: SUGGESTED[r] };
}

export function letterPresetForRound(round) {
  const keys = ROUND_PRESETS[round];
  if (!keys) return null;
  const all = [...ROUND_TYPES[1], ...ROUND_TYPES[2], ...ROUND_TYPES[3]].filter((k) => k !== "cfpb");
  return Object.fromEntries(all.map((k) => [k, keys.includes(k)]));
}

export function letterStatus(doc, mailingsById = {}, now = new Date()) {
  const s = doc?.stage;
  if (s === "Resolved") return LS.DONE;
  if (s === "Response Received" || s === "Follow-Up Needed") return LS.RESPONSE;
  if (s === "Dispute Sent") {
    const m = doc.mailing_id ? mailingsById[doc.mailing_id] : null;
    const eta = m?.expected_delivery_date ? new Date(m.expected_delivery_date) : null;
    if (eta && eta > now) return LS.MAILED;
    const due = doc.followup_due ? new Date(doc.followup_due) : null;
    if (due && due <= now && roundOf(doc) < 3) return LS.NEXT;
    return LS.WAITING;
  }
  return LS.READY;
}

const unlocksNext = (items) => items.some((i) => [LS.RESPONSE, LS.NEXT, LS.DONE].includes(i.status));

function aggregate(items) {
  const has = (s) => items.some((i) => i.status === s);
  if (items.every((i) => i.status === LS.DONE)) return "completed";
  if (has(LS.READY)) return "ready";
  if (has(LS.RESPONSE)) return "response";
  if (has(LS.MAILED)) return "mailed";
  return "waiting";
}

function roundStates(byRound, setupDone) {
  const unlocked = {
    1: setupDone,
    2: byRound[2].length > 0 || unlocksNext(byRound[1]),
    3: byRound[3].length > 0 || unlocksNext(byRound[2]),
  };
  const states = {};
  for (const r of [1, 2, 3]) {
    const items = byRound[r];
    const laterStarted = [1, 2, 3].some((x) => x > r && byRound[x].length > 0);
    if (items.length === 0) states[r] = unlocked[r] ? (r === 1 ? "current" : "available") : "not_started";
    else if (laterStarted && !items.some((i) => i.status === LS.READY)) states[r] = "completed";
    else states[r] = aggregate(items);
  }
  return { unlocked, states };
}

function earliestDue(items) {
  const dues = items.map((i) => i.doc.followup_due).filter(Boolean).map((d) => new Date(d)).sort((a, b) => a - b);
  return dues[0] || null;
}

function nextStep({ identityReady, latestReport, current, byRound, unlocked, states, cfpbDrafts }) {
  if (!identityReady) {
    return { key: "identity", title: "Upload your identity documents", body: "Credit bureaus need a copy of your photo ID and proof of address with every dispute letter.", cta: "Upload Identity Documents", to: "/app/identity" };
  }
  if (!latestReport) {
    return { key: "report", title: "Upload your credit report", body: "Add a recent credit report so we can find the accounts you want to dispute.", cta: "Upload Credit Report", to: "/app/upload" };
  }
  if (!current) {
    return { key: "start_1", title: "Start Round 1", body: "Choose the accounts you want to dispute and we'll prepare your Round 1 validation letters.", cta: "Start Round 1", to: `/app/review/${latestReport.id}?round=1` };
  }
  const items = byRound[current];
  if (states[current] === "ready") {
    const signed = items.some((i) => i.status === LS.READY && i.doc.signature_consent_at);
    return signed
      ? { key: "mail", title: `Your Round ${current} letters are ready to mail`, body: "Review each letter one last time, then approve and mail it.", cta: "Review and Mail Letters", to: "/app/documents" }
      : { key: "review", title: `Review your Round ${current} letters`, body: "Read each letter, make any edits, then sign and mail it.", cta: `Review Round ${current} Letters`, to: "/app/documents" };
  }
  if (current < 3 && unlocked[current + 1]) {
    const reportId = items.find((i) => i.doc.report_id)?.doc.report_id || latestReport.id;
    const isTwo = current + 1 === 2;
    return {
      key: isTwo ? "start_2" : "start_3",
      title: isTwo ? "You're ready for Round 2" : "You're ready for the evidence-based round",
      body: isTwo ? "Ask how the bureaus verified any account they kept on your report." : "Use the responses and documents you've collected to prepare an escalated dispute.",
      cta: isTwo ? "Start Round 2" : "Start Evidence-Based Round",
      to: `/app/review/${reportId}?round=${current + 1}`,
    };
  }
  const sent = items.some((i) => i.status !== LS.READY);
  if (current === 3 && sent && cfpbDrafts.length === 0) {
    return { key: "cfpb", title: "Prepare your CFPB complaint", body: "If the issue is still unresolved, you can file a complaint with the Consumer Financial Protection Bureau.", cta: "Prepare CFPB Complaint", to: "/app/cfpb" };
  }
  if (states[current] === "completed") {
    return { key: "done", title: "You're all caught up", body: "All of your letters are marked complete. Check your letter status any time.", cta: "View Letter Status", to: "#letter-status" };
  }
  const due = earliestDue(items);
  return {
    key: "waiting",
    title: states[current] === "mailed" ? `Your Round ${current} letters are in the mail` : `Waiting for Round ${current} responses`,
    body: "Credit bureaus usually have 30 days to investigate a dispute. When a response arrives, update the letter's status on the Documents page.",
    due: due ? due.toISOString() : null,
    cta: "View Letter Status",
    to: "#letter-status",
  };
}

export function buildPlan({ identity, reports = [], docs = [], mailings = [], cfpbDrafts = [], now = new Date() }) {
  const mailingsById = Object.fromEntries(mailings.map((m) => [m.id, m]));
  const items = docs
    .map((doc) => ({ doc, round: roundOf(doc), status: letterStatus(doc, mailingsById, now) }))
    .filter((i) => i.round);
  const byRound = { 1: [], 2: [], 3: [] };
  items.forEach((i) => byRound[i.round].push(i));
  const identityReady = !!identity?.ready_for_mailing;
  const latestReport = reports[0] || null;
  const { unlocked, states } = roundStates(byRound, identityReady && !!latestReport);
  const current = [3, 2, 1].find((r) => byRound[r].length > 0) || null;
  const setup = [
    { key: "report", label: "Upload Credit Report", done: !!latestReport, to: "/app/upload", required: true },
    { key: "id_front", label: "Photo ID — Front", done: !!identity?.id_front, to: "/app/identity", required: true },
    { key: "id_back", label: "Photo ID — Back, if applicable", done: !!identity?.id_back, to: "/app/identity", required: false },
    { key: "proof_of_address", label: "Proof of Address", done: !!identity?.proof_of_address, to: "/app/identity", required: true },
  ];
  return {
    setup,
    items,
    rounds: [1, 2, 3].map((r) => ({ round: r, state: states[r], locked: !unlocked[r] && r > 1, count: byRound[r].length })),
    currentRound: current,
    cfpbUnlocked: unlocked[3],
    cfpbCount: cfpbDrafts.length,
    next: nextStep({ identityReady, latestReport, current, byRound, unlocked, states, cfpbDrafts }),
    latestReport,
  };
}
